import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMonths, startOfMonth } from 'date-fns';
import { useGetVenuaPlannerTree } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { Alert, Panel, Skeleton, TreeView } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { calendarAgenda } from './calendarAgenda';
import { formatMonthParam, matchesTransportFilter, parseMonthParam } from './calendarModel';
import {
  buildCalendarSerieOptions,
  buildCalendarTreeData,
  collectTreeTermIds,
} from './calendarTreeModel';
import { useCalendarTerms } from './useCalendarTerms';
import { CalendarLegend } from './components/CalendarLegend';
import { CalendarToolbar } from './components/CalendarToolbar';
import { CreateTermsPopover } from './components/CreateTermsPopover';
import { MonthGrid } from './components/MonthGrid';
import { TermDrawer } from './components/TermDrawer';

interface CalendarPageProps {
  layerId: string;
}

/**
 * Agenda „Kalendář zájezdů“ — měsíční kalendář termínů venua planneru.
 *
 * Vlevo strom projekt → produkt → série → termín s checkboxy (zobrazit/skrýt v kalendáři),
 * uprostřed měsíční grid s pruhy termínů a jejich dopravou, vpravo drawer s rozpadem termínu.
 * „+“ na dni otevře popover pro založení termínů do vybrané série (GenerateTerms, režim Calendar).
 */
export function CalendarPage({ layerId }: CalendarPageProps) {
  const { t } = useTranslation('planner');
  const { state } = useAgendaParams(calendarAgenda, layerId);
  const agendaState = useAgendaState(calendarAgenda, layerId);

  const month = useMemo(
    () => (state.month ? parseMonthParam(state.month) : startOfMonth(new Date())),
    [state.month]
  );
  const transport = state.transport ?? 'all';

  const treeQuery = useGetVenuaPlannerTree();
  const { events, isLoading, isError, error } = useCalendarTerms(month);

  // Zaškrtnuté termíny = lokální stav (množina UUID je příliš velká pro URL).
  // První načtení stromu zaškrtne vše; později se nově vzniklé termíny automaticky přidávají.
  const [checkedTermIds, setCheckedTermIds] = useState<ReadonlySet<string>>(new Set());
  const knownTermIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!treeQuery.data) return;
    const allTermIds = collectTreeTermIds(treeQuery.data);
    const newIds = allTermIds.filter((id) => !knownTermIdsRef.current.has(id));
    if (newIds.length === 0) return;
    knownTermIdsRef.current = new Set(allTermIds);
    setCheckedTermIds((prev) => {
      const next = new Set(prev);
      for (const id of newIds) next.add(id);
      return next;
    });
  }, [treeQuery.data]);

  const treeData = useMemo(
    () =>
      buildCalendarTreeData(treeQuery.data ?? { projects: [] }, {
        project: t('tree.types.project'),
        product: t('tree.types.product'),
        serie: t('tree.types.serie'),
        term: t('tree.types.term'),
      }),
    [treeQuery.data, t]
  );

  const serieOptions = useMemo(
    () => (treeQuery.data ? buildCalendarSerieOptions(treeQuery.data) : []),
    [treeQuery.data]
  );

  const visibleEvents = useMemo(
    () =>
      events.filter(
        (event) =>
          checkedTermIds.has(event.termId) && matchesTransportFilter(event.transport, transport)
      ),
    [events, checkedTermIds, transport]
  );

  const drawerEvent = useMemo(
    () => (state.termId ? events.find((event) => event.termId === state.termId) : undefined),
    [events, state.termId]
  );

  const goToMonth = useCallback(
    (date: Date) => agendaState.patch({ month: formatMonthParam(date) }),
    [agendaState]
  );

  const handleEventClick = useCallback(
    (termId: string) => agendaState.patch({ termId }),
    [agendaState]
  );

  const closeDrawer = useCallback(() => agendaState.remove('termId'), [agendaState]);

  const renderDayAction = useCallback(
    (isoDay: string) =>
      serieOptions.length > 0 ? <CreateTermsPopover date={isoDay} series={serieOptions} /> : null,
    [serieOptions]
  );

  return (
    <div className="bg-background gap-phx-4 flex h-full min-h-0 p-4" data-testid="planner-calendar">
      <Panel title={t('calendar.sidebar.title')} className="w-80 shrink-0">
        {treeQuery.isLoading ? (
          <Skeleton className="h-40" />
        ) : treeQuery.isError ? (
          <Alert severity="error" title={t('states.errorTitle')}>
            {t('states.errorDescription')}
          </Alert>
        ) : (
          <TreeView
            data={treeData}
            checkable
            checkedIds={checkedTermIds}
            onCheckedChange={setCheckedTermIds}
            defaultExpandedIds={treeData.rootIds}
          />
        )}
      </Panel>

      <div className="gap-phx-3 relative flex min-h-0 min-w-0 flex-1 flex-col">
        <CalendarToolbar
          month={month}
          transport={transport}
          onPrevMonth={() => goToMonth(addMonths(month, -1))}
          onNextMonth={() => goToMonth(addMonths(month, 1))}
          onToday={() => goToMonth(startOfMonth(new Date()))}
          onTransportChange={(filter) => agendaState.patch({ transport: filter })}
        />

        {/* Kostra měsíčního gridu je vidět vždy (závisí jen na `month`); načítání
            i chyba termínů se hlásí neblokujícím banderem nad ní, bez skrytí gridu. */}
        {isError ? (
          <Alert severity="error" title={t('states.errorTitle')}>
            {error instanceof Error ? error.message : t('states.errorDescription')}
          </Alert>
        ) : isLoading ? (
          <Skeleton className="h-8 shrink-0" />
        ) : null}
        <MonthGrid
          month={month}
          events={isError ? [] : visibleEvents}
          onEventClick={handleEventClick}
          renderDayAction={renderDayAction}
        />

        <CalendarLegend />

        {drawerEvent && <TermDrawer event={drawerEvent} onClose={closeDrawer} />}
      </div>
    </div>
  );
}
